import React from "react";
import { Bell, Calendar, Check, CheckCircle2, Layers, MapPin, RefreshCw, Search, Star } from "lucide-react";

const CORE_STEPS = [
  {
    eyebrow: "DISCOVER",
    title: "Discover",
    color: "#017bef",
    icon: Search,
    description: "Find workspaces, accommodation and experiences — all through one unified search.",
  },
  {
    eyebrow: "BOOK",
    title: "Book",
    color: "#0062c0",
    icon: Calendar,
    description: "Check real-time availability and book instantly, without leaving the platform.",
  },
  {
    eyebrow: "MANAGE",
    title: "Manage",
    color: "#012d6e",
    icon: Layers,
    description: "Keep all your bookings and reservations organised in one place.",
  },
];

export function PlatformCore() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <p className="text-center text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">
          Core Platform
        </p>
        <h2 className="pt-[12px] text-center text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
          Everything Connected in One Place
        </h2>

        <div className="grid grid-cols-1 gap-6 pt-14 lg:grid-cols-3">
          {CORE_STEPS.map(({ icon: Icon, ...step }) => (
            <div key={step.title} className="rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] p-8">
              <span
                className="flex size-12 items-center justify-center rounded-2xl shadow-sm"
                style={{ backgroundColor: step.color }}
              >
                <Icon className="size-5 text-white" />
              </span>
              <p className="pt-[20px] text-[10px] font-bold leading-[15px] tracking-[1px] text-[#717182]">
                {step.eyebrow}
              </p>
              <h3 className="pt-2 text-[20px] font-bold leading-7 text-[#0a0a0a]">{step.title}</h3>
              <p className="pt-[12px] text-[14px] leading-[22.75px] text-[#717182]">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const SEARCH_POINTS = [
  "Search across Work, Stay and Play simultaneously",
  "Filter by category, location and availability",
  "One search. Every space.",
];

const SEARCH_RESULTS = [
  { name: "Space listing one", status: "Available" },
  { name: "Space listing two", status: "Available" },
  { name: "Space listing three", status: "Unavailable" },
];

export function PlatformSearch() {
  return (
    <section className="bg-[#f5f8ff] py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-[16px] sm:px-8 lg:grid-cols-2">
        <div>
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">Feature 01</p>
          <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
            Find Everything in One Place
          </h2>
          <p className="pt-[16px] text-[18px] leading-[29.25px] text-[#717182]">
            Unified search and discovery across Work, Stay and Play. Discover and book workspaces, accommodation and
            experiences without switching between apps.
          </p>
          <ul className="space-y-[12px] pt-8">
            {SEARCH_POINTS.map(point => (
              <li key={point} className="flex items-start gap-[12px] text-[14px] leading-5 text-[#717182]">
                <Check className="mt-0.5 size-3.5 shrink-0 text-[#017bef]" />
                {point}
              </li>
            ))}
          </ul>
        </div>

        {/* Illustration of the search experience */}
        <div
          aria-hidden
          className="overflow-hidden rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-white shadow-[0_8px_40px_0_rgba(0,0,0,0.06)]"
        >
          <div className="flex border-b-[1px] border-solid border-[#e5e7eb]">
            {["WORK", "STAY", "PLAY"].map((tab, index) => (
              <span
                key={tab}
                className={`flex-1 py-3.5 text-center text-[12px] font-bold leading-4 tracking-[1.2px] ${
                  index === 0
                    ? "border-b-2 border-solid border-[#017bef] bg-[#f0f7ff] text-[#017bef]"
                    : "text-[#717182]"
                }`}
              >
                {tab}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-[12px] border-b-[1px] border-solid border-[#f5f8ff] px-[20px] py-[16px]">
            <Search className="size-[15px] text-[#017bef]" />
            <span className="flex-1 text-[14px] leading-5 text-[#717182]">Search work spaces…</span>
            <MapPin className="size-[13px] text-[#717182]" />
          </div>
          {SEARCH_RESULTS.map(result => {
            const available = result.status === "Available";
            return (
              <div
                key={result.name}
                className="flex items-center justify-between border-b-[1px] border-solid border-[#f5f8ff] px-[20px] py-3.5 last:border-b-0"
              >
                <div className="flex items-center gap-[12px]">
                  <span className="size-8 rounded-[14px] border-[1px] border-solid border-[#e5e7eb] bg-[#f5f8ff]" />
                  <div>
                    <p className="text-[12px] font-semibold leading-4 text-[#0a0a0a]">{result.name}</p>
                    <p className="text-[10px] leading-[15px] text-[#717182]">Work</p>
                  </div>
                </div>
                <span className="flex items-center gap-2">
                  <span className={`size-2 rounded-full ${available ? "bg-[#22c55e]" : "bg-[#e5e7eb]"}`} />
                  <span
                    className={`text-[10px] font-semibold leading-[15px] ${
                      available ? "text-[#16a34a]" : "text-[#9ca3af]"
                    }`}
                  >
                    {result.status}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Availability grid from the design: true = open
const AVAILABILITY = [true, true, false, true, true, true, false, false, true, true];

export function PlatformAvailabilityAndPayment() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-[16px] sm:px-8 lg:grid-cols-2">
        <div className="rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] p-8">
          <p className="text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-[#017bef]">Feature 02</p>
          <h2 className="pt-[12px] text-[24px] font-bold leading-8 tracking-[-0.6px] text-[#0a0a0a]">
            Know What's Available
          </h2>
          <p className="pt-[16px] text-[14px] leading-[22.75px] text-[#717182]">
            Real-time availability — see what's open before you book.
          </p>

          <div aria-hidden className="mt-8 rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white p-[20px]">
            <p className="text-[12px] font-bold leading-4 text-[#0a0a0a]">Availability</p>
            <div className="grid grid-cols-5 gap-2 pt-[16px]">
              {AVAILABILITY.map((open, index) => (
                <span
                  key={index}
                  className={`flex h-9 items-center justify-center rounded-[14px] border-[1px] border-solid ${
                    open ? "border-[#86efac] bg-[#f0fdf4]" : "border-[#e5e7eb] bg-[#f5f5f5]"
                  }`}
                >
                  <span className={`size-2 rounded-full ${open ? "bg-[#22c55e]" : "bg-[#e5e7eb]"}`} />
                </span>
              ))}
            </div>
            <div className="mt-[16px] flex gap-[20px] border-t-[1px] border-solid border-[#f5f8ff] pt-[12px]">
              {[
                { label: "Available", dot: "bg-[#22c55e]" },
                { label: "Unavailable", dot: "bg-[#e5e7eb]" },
              ].map(legend => (
                <span
                  key={legend.label}
                  className="flex items-center gap-1.5 text-[10px] leading-[15px] text-[#717182]"
                >
                  <span className={`size-2 rounded-full ${legend.dot}`} />
                  {legend.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-3xl bg-[#012d6e] p-8">
          <div className="pointer-events-none absolute right-0 top-0 size-48 rounded-full bg-[#017bef]/20 blur-[64px]" />
          <div className="relative">
            <p className="text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-white/50">Feature 03</p>
            <h2 className="pt-[12px] text-[24px] font-bold leading-8 tracking-[-0.6px] text-white">
              Book and Pay Securely
            </h2>
            <p className="pt-[16px] text-[14px] leading-[22.75px] text-white/60">
              Secure online payments, built into every booking.
            </p>

            <div aria-hidden className="flex flex-wrap items-center gap-[12px] pt-8">
              {["Booking", "Payment"].map(step => (
                <span key={step} className="flex items-center gap-[12px]">
                  <span className="rounded-[14px] bg-[#017bef] px-[12px] py-2 text-[12px] font-semibold leading-4 text-white">
                    {step}
                  </span>
                  <span className="text-white/40">›</span>
                </span>
              ))}
              <span className="rounded-[14px] bg-white/10 px-[12px] py-2 text-[12px] font-semibold leading-4 text-white/60">
                Confirmation
              </span>
            </div>

            <div
              aria-hidden
              className="mt-[20px] flex items-center gap-[12px] rounded-2xl border-[1px] border-solid border-white/[0.12] bg-white/[0.08] p-[16px]"
            >
              <span className="flex size-8 items-center justify-center rounded-full bg-[#22c55e]">
                <Check className="size-3.5 text-white" />
              </span>
              <div>
                <p className="text-[14px] font-semibold leading-5 text-white">Booking confirmed</p>
                <p className="text-[12px] leading-4 text-white/40">Payment secure</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const TRUST_POINTS = [
  "Every listing is verified before going live",
  "User reviews help you choose with confidence",
  "Consistent quality across Work, Stay and Play",
];

export function PlatformTrust() {
  return (
    <section className="bg-[#f5f8ff] py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-[16px] sm:px-8 lg:grid-cols-2">
        {/* Illustration of a verified listing */}
        <div
          aria-hidden
          className="overflow-hidden rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-white shadow-[0_4px_24px_0_rgba(0,0,0,0.05)]"
        >
          <div className="h-36 bg-gradient-to-br from-[#e8f4ff] to-[#f0f8ff]" />
          <div className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-[16px] font-bold leading-6 text-[#0a0a0a]">Listing title</p>
              <span className="flex items-center gap-1 rounded-full border-[1px] border-solid border-[#86efac] bg-[#f0fdf4] px-2.5 py-1 text-[10px] font-bold leading-[15px] text-[#16a34a]">
                <Check className="size-2.5" />
                Verified
              </span>
            </div>
            <p className="pt-[12px] text-[14px] leading-5 text-[#717182]">Space description placeholder</p>

            <div className="mt-[16px] border-t-[1px] border-solid border-[#f5f8ff] pt-[16px]">
              <p className="text-[12px] font-bold leading-4 text-[#0a0a0a]">Reviews</p>
              {[1, 2].map(review => (
                <div key={review} className="flex items-start gap-2.5 pt-[12px]">
                  <span className="size-6 rounded-full border-[1px] border-solid border-[#e5e7eb] bg-[#f5f8ff]" />
                  <div className="flex-1">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star key={star} className="size-2.5 fill-[#017bef] text-[#017bef]" />
                      ))}
                    </div>
                    <span className="mt-1 block h-2 w-3/4 rounded-full bg-[#f5f8ff]" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">Feature 04</p>
          <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
            Know What to Expect
          </h2>
          <p className="pt-[16px] text-[18px] leading-[29.25px] text-[#717182]">
            Verified listings and reviews across every space and venue on the SPACIA platform.
          </p>
          <ul className="space-y-[12px] pt-8">
            {TRUST_POINTS.map(point => (
              <li key={point} className="flex items-start gap-[12px] text-[14px] leading-5 text-[#717182]">
                <Check className="mt-0.5 size-3.5 shrink-0 text-[#017bef]" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

const NOTIFICATIONS = [
  {
    icon: CheckCircle2,
    tint: "border-[#86efac] bg-[#f0fdf4] text-[#16a34a]",
    title: "Booking confirmation",
    body: "Your booking is confirmed",
  },
  {
    icon: Bell,
    tint: "border-[#bfdbfe] bg-[#f0f7ff] text-[#017bef]",
    title: "Reminder",
    body: "Your booking is coming up",
  },
  {
    icon: RefreshCw,
    tint: "border-[#bfdbfe] bg-[#f0f7ff] text-[#017bef]",
    title: "Update",
    body: "An update on your reservation",
  },
];

export function PlatformBookingsAndUpdates() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-[16px] sm:px-8 lg:grid-cols-2">
        <div>
          <p className="text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-[#017bef]">Feature 05</p>
          <h2 className="pt-[12px] text-[24px] font-bold leading-8 tracking-[-0.6px] text-[#0a0a0a]">
            All Your Bookings in One Place
          </h2>
          <p className="pt-[12px] text-[14px] leading-[22.75px] text-[#717182]">
            Booking management and history — every reservation, across Work, Stay and Play.
          </p>

          {/* Illustration of the bookings list */}
          <div
            aria-hidden
            className="mt-[20px] overflow-hidden rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-white shadow-[0_8px_40px_0_rgba(0,0,0,0.06)]"
          >
            <div className="flex border-b-[1px] border-solid border-[#e5e7eb]">
              {["UPCOMING", "PAST"].map((tab, index) => (
                <span
                  key={tab}
                  className={`flex-1 py-3.5 text-center text-[12px] font-bold leading-4 tracking-[1.2px] ${
                    index === 0
                      ? "border-b-2 border-solid border-[#017bef] bg-[#f0f7ff] text-[#017bef]"
                      : "text-[#717182]"
                  }`}
                >
                  {tab}
                </span>
              ))}
            </div>
            {[
              { label: "WORK", color: "#017bef", name: "Coworking space", note: "Next booking" },
              { label: "STAY", color: "#0062c0", name: "Hotel stay", note: "Upcoming" },
            ].map(booking => (
              <div
                key={booking.name}
                className="flex items-center justify-between border-b-[1px] border-solid border-[#f5f8ff] px-[20px] py-[16px] last:border-b-0"
              >
                <span className="flex items-center gap-[12px]">
                  <span
                    className="rounded-[10px] px-2 py-1 text-[9px] font-bold leading-[13.5px] text-white"
                    style={{ backgroundColor: booking.color }}
                  >
                    {booking.label}
                  </span>
                  <span className="text-[14px] font-semibold leading-5 text-[#0a0a0a]">{booking.name}</span>
                </span>
                <span className="text-[12px] leading-4 text-[#717182]">{booking.note}</span>
              </div>
            ))}
            <p className="border-t-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] px-[20px] py-[12px] text-center text-[12px] leading-4 text-[#717182]">
              Manage all bookings in one place
            </p>
          </div>
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-[#017bef]">Feature 06</p>
          <h2 className="pt-[12px] text-[24px] font-bold leading-8 tracking-[-0.6px] text-[#0a0a0a]">
            Stay Up to Date
          </h2>
          <p className="pt-[12px] text-[14px] leading-[22.75px] text-[#717182]">
            Automated notifications and reminders so you're always informed.
          </p>

          <div aria-hidden className="space-y-[12px] pt-[20px]">
            {NOTIFICATIONS.map(({ icon: Icon, ...note }) => (
              <div
                key={note.title}
                className="flex items-start gap-[16px] rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white px-[20px] py-[16px]"
              >
                <span
                  className={`flex size-9 items-center justify-center rounded-[14px] border-[1px] border-solid ${note.tint}`}
                >
                  <Icon className="size-[13px]" />
                </span>
                <div>
                  <p className="text-[14px] font-semibold leading-5 text-[#0a0a0a]">{note.title}</p>
                  <p className="text-[12px] leading-4 text-[#717182]">{note.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
